// Authentication utilities for token management

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const USER_KEY = "user-streakspark";

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  name: string;
  sparkPoints?: number; // Optional since API might not return it initially
  isEmailVerified?: boolean; // Email verification status
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

// Store tokens in localStorage
export const storeAuthData = (data: LoginResponse): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
};

// Retrieve access token
export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

// Retrieve refresh token
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

// Retrieve user data
export const getStoredUser = (): AuthUser | null => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

// Clear all auth data (logout)
export const clearAuthData = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Check if access token is expired
export const isTokenExpired = (): boolean => {
  const token = getAccessToken();
  if (!token) return true;

  try {
    // JWT tokens have 3 parts separated by '.'
    const payload = token.split(".")[1];
    // Decode the payload
    const decodedPayload = JSON.parse(atob(payload));
    // Check if token has expired
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedPayload.exp < currentTime;
  } catch (error) {
    // If token is malformed, consider it expired
    console.error("Error parsing token:", error);
    return true;
  }
};

// Enhanced authentication check that considers token expiration
export const isAuthenticatedAndValid = (): boolean => {
  return isAuthenticated() && !isTokenExpired();
};

// Handle token expiration - clear data and redirect to login
export const handleTokenExpiration = (): void => {
  console.log("Token expired, clearing authentication data");
  clearAuthData();
  // Redirect to login page with returnUrl if not already on login
  if (window.location.pathname !== '/login') {
    const currentPath = window.location.pathname + window.location.search;
    const returnUrl = encodeURIComponent(currentPath);
    window.location.href = `/login?returnUrl=${returnUrl}`;
  }
};

// Get authorization header for API calls
export const getAuthHeader = (): { Authorization: string } | {} => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Determine if the app is running as an installed PWA or web app
export const getAppType = (): 'web' | 'device' => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  ) ? 'device' : 'web';
};
